import * as L from 'leaflet';
import * as topojson from 'topojson-client';
import topoDataEng from '../data/regions-england.json';
import topoDataWal from '../data/regions-wales.json';
import topoDataScot from '../data/regions-scotland.json';
import geoDataNI from '../data/regions-nireland.json';
import iconAsparagus from '../images/icons/asparagus-icon.png';
import filterIcon from '../images/icons/filter-icon.svg';
const greenIcon = L.icon({
    iconUrl: iconAsparagus,
    iconSize: [60, 60],
    iconAnchor: [30, 60],
    popupAnchor: [-3, -76]
});
// Filter Toggle Button (mobile only)
class FilterToggleButton extends L.Control {
    constructor(options) {
        super(options);
        this._onToggle = options.onToggle;
    }
    onAdd(map) {
        this._div = L.DomUtil.create('div', 'filter-toggle-button');
        this._div.innerHTML = `
      <button type="button" class="filter-toggle-button__btn" aria-label="Toggle filters" title="Toggle filters">
        <img src="${filterIcon}" alt="Filter icon" />
      </button>
    `;
        L.DomEvent.disableClickPropagation(this._div);
        const button = this._div.querySelector('.filter-toggle-button__btn');
        button.addEventListener('click', () => {
            this._onToggle();
        });
        return this._div;
    }
}
// Filter Control for filtering farms
class FilterControl extends L.Control {
    constructor(options) {
        super(options);
        this._regions = options.regions;
        this._onFilterChange = options.onFilterChange;
    }
    onAdd(map) {
        this._div = L.DomUtil.create('div', 'farm-pane filter-pane');
        const regionOptions = this._regions
            .map(region => `<option value="${region.value}">${region.label}</option>`)
            .join('');
        this._div.innerHTML = `
      <div class="farm-pane__body">
        <div style="margin-bottom: 10px;">
          <label style="display: block; margin-bottom: 4px; font-size: 12px;">Type:</label>
          <select id="type-filter" style="width: 100%; padding: 4px;">
            <option value="all">Growers & Sellers</option>
            <option value="grower">Growers Only</option>
            <option value="seller">Sellers Only</option>
          </select>
        </div>
        <div style="margin-bottom: 10px;">
          <label style="display: block; margin-bottom: 4px; font-size: 12px;">Region:</label>
          <select id="region-filter" style="width: 100%; padding: 4px;">
            <option value="all">All Regions</option>
            ${regionOptions}
          </select>
        </div>
        <div>
          <label style="display: block; margin-bottom: 4px; font-size: 12px;">Availability:</label>
          <select id="season-filter" style="width: 100%; padding: 4px;">
            <option value="all">All Farms</option>
            <option value="current">Available Now</option>
            <option value="4">April</option>
            <option value="5">May</option>
            <option value="6">June</option> 
            <option value="7">July</option>
          </select>
        </div>
      </div>
    `;
        L.DomEvent.disableClickPropagation(this._div);
        L.DomEvent.disableScrollPropagation(this._div);
        const typeFilter = this._div.querySelector('#type-filter');
        const regionFilter = this._div.querySelector('#region-filter');
        const seasonFilter = this._div.querySelector('#season-filter');
        const fireChange = () => {
            this._onFilterChange({
                region: regionFilter.value === 'all' ? 'all' : { value: regionFilter.value, label: regionFilter.options[regionFilter.selectedIndex].text },
                season: seasonFilter.value,
                type: typeFilter.value
            });
        };
        typeFilter.addEventListener('change', fireChange);
        regionFilter.addEventListener('change', fireChange);
        seasonFilter.addEventListener('change', fireChange);
        return this._div;
    }
    toggle() {
        this._div.classList.toggle('is-hidden');
    }
}
// Info Control for displaying region/farm data
class InfoControl extends L.Control {
    onAdd(map) {
        this._div = L.DomUtil.create('div', 'farm-pane info-pane info is-hidden');
        L.DomEvent.disableClickPropagation(this._div);
        L.DomEvent.disableScrollPropagation(this._div);
        return this._div;
    }
    update(data) {
        var _a;
        if (!data) {
            this._div.classList.add('is-hidden');
            this._div.innerHTML = '';
            return;
        }
        this._div.classList.remove('is-hidden');
        if (data.type === 'region') {
            const props = data.content;
            this._div.innerHTML = `
        <h4>Region</h4>
        <strong>${props.name || 'Unknown Region'}</strong><br/>
        ${props.country ? `<em>${props.country}</em>` : ''}
      `;
        }
        else if (data.type === 'farm') {
            const farm = data.content;
            const acf = farm.acf;
            const seasonRange = formatSeasonRange(acf.season_start, acf.season_end);
            const farmType = acf.farm_type || 'grower';
            const typeLabel = farmType === 'seller' ? 'Seller' : 'Grower';
            const img = ((_a = acf.grower_image) === null || _a === void 0 ? void 0 : _a.url)
                ? `<img src="${acf.grower_image.url}" style="width:100%;display:block;margin:0 0 8px;" />`
                : '';
            this._div.innerHTML = `
        ${img}
        <h4>${farm.title.rendered}</h4>
        <span class="farm-type-badge" style="display:inline-block;padding:2px 8px;background:#02AAB0;color:white;border-radius:4px;font-size:11px;margin-bottom:8px;">${typeLabel}</span>
        <div class="grower-description">${acf.grower_description || ''}</div>
        <span>Region: ${acf.region}</span>
        <span>Season: ${seasonRange}</span>
      `;
        }
    }
}
export function initFarmMap(mapId = 'farms-map') {
    const mapEl = document.getElementById(mapId);
    if (!mapEl)
        return;
    // Fetch both farms and region choices in parallel
    Promise.all([
        fetch('/wp-json/wp/v2/whd-farms?per_page=100').then(res => res.json()),
        fetch('/wp-json/acf/v1/field-choices/field_6985e16ffa3db').then(res => res.json())
    ])
        .then(([farms, regionChoices]) => {
        // Convert ACF choices object to RegionData array
        const regions = Object.entries(regionChoices).map(([value, label]) => ({
            value,
            label
        }));
        // Transform farm region data from strings to RegionData objects
        const transformedFarms = farms.map(farm => {
            if (farm.acf.region && Array.isArray(farm.acf.region)) {
                farm.acf.region = farm.acf.region.map((regionValue) => {
                    // If it's already an object with label/value, keep it
                    if (typeof regionValue === 'object' && regionValue.label) {
                        return regionValue;
                    }
                    // Otherwise, it's a string value - look up the label
                    const regionData = regions.find(r => r.value === regionValue);
                    return regionData || { value: regionValue, label: regionValue };
                });
            }
            return farm;
        });
        const map = L.map(mapId, { scrollWheelZoom: false, zoomControl: false }).setView([54.5, -3], 6);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
        }).addTo(map);
        L.control.zoom({ position: 'topright' }).addTo(map);
        const farmMarkers = [];
        const info = new InfoControl({ position: 'topleft' });
        const regionLayers = [];
        const regionSets = [
            { data: topoDataEng, name: 'England', country: 'England' },
            { data: topoDataWal, name: 'Wales', country: 'Wales' },
            { data: topoDataScot, name: 'Scotland', country: 'Scotland' }
        ];
        regionSets.forEach(regionSet => {
            const topoObjects = regionSet.data.objects;
            const firstObject = Object.values(topoObjects)[0];
            const geojsonFeatures = topojson.feature(regionSet.data, firstObject);
            L.geoJSON(geojsonFeatures, {
                style: {
                    color: '#02AAB0',
                    weight: 2,
                    fillOpacity: 0.2
                },
                onEachFeature: (feature, layer) => {
                    var _a, _b, _c;
                    layer.regionData = {
                        name: ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.name) || ((_b = feature.properties) === null || _b === void 0 ? void 0 : _b.EER13NM) || regionSet.name,
                        slug: ((_c = feature.properties) === null || _c === void 0 ? void 0 : _c.slug) || regionSet.name.toLowerCase(),
                        country: regionSet.country
                    };
                    layer.on({
                        mouseover: highlightFeature,
                        mouseout: resetHighlight,
                        click: (e) => {
                            zoomToFeature(e);
                            const regionData = e.target.regionData;
                            info.update({ type: 'region', content: regionData });
                        }
                    });
                    regionLayers.push(layer);
                }
            }).addTo(map);
        });
        L.geoJSON(geoDataNI, {
            style: {
                color: '#02AAB0',
                weight: 2,
                fillOpacity: 0.2
            },
            onEachFeature: (feature, layer) => {
                var _a;
                layer.regionData = {
                    name: ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.name) || 'Northern Ireland',
                    slug: 'northern-ireland',
                    country: 'Northern Ireland'
                };
                layer.on({
                    mouseover: highlightFeature,
                    mouseout: resetHighlight,
                    click: (e) => {
                        zoomToFeature(e);
                        const regionData = e.target.regionData;
                        info.update({ type: 'region', content: regionData });
                    }
                });
                regionLayers.push(layer);
            }
        }).addTo(map);
        transformedFarms.forEach(farm => {
            const acf = farm.acf;
            const loc = acf.grower_location;
            if (!loc || !loc.lat || !loc.lng)
                return;
            const marker = L.marker([loc.lat, loc.lng], { icon: greenIcon }).addTo(map);
            marker.on('click', () => {
                info.update({ type: 'farm', content: farm });
            });
            farmMarkers.push({ marker, farm });
        });
        const filterControl = new FilterControl({
            position: 'topleft',
            regions: regions,
            onFilterChange: (filters) => {
                farmMarkers.forEach(({ marker, farm }) => {
                    const acf = farm.acf;
                    let show = true;
                    // Filter by type
                    if (filters.type !== 'all') {
                        const farmType = acf.farm_type || 'grower'; // Default to grower if not set
                        show = farmType === filters.type;
                    }
                    if (show && filters.region !== 'all') {
                        const selectedRegionValue = typeof filters.region === 'string' ? filters.region : filters.region.value;
                        if (Array.isArray(acf.region)) {
                            show = acf.region.some(r => normalizeRegion(r) === selectedRegionValue.toLowerCase());
                        }
                        else if (acf.region) {
                            show = normalizeRegion(acf.region) === selectedRegionValue.toLowerCase();
                        }
                        else {
                            show = false;
                        }
                    }
                    if (show && filters.season !== 'all') {
                        const currentMonth = getCurrentMonth();
                        show = isMonthInRange(currentMonth, acf.season_start, acf.season_end);
                    }
                    if (show) {
                        marker.addTo(map);
                    }
                    else {
                        marker.remove();
                    }
                });
            }
        });
        const filterToggle = new FilterToggleButton({
            position: 'topright',
            onToggle: () => {
                filterControl.toggle();
            }
        });
        filterToggle.addTo(map);
        filterControl.addTo(map);
        info.addTo(map);
    });
}
function highlightFeature(e) {
    const layer = e.target;
    layer.setStyle({
        weight: 4,
        color: '#9D50BB',
        dashArray: '',
        fillOpacity: 0.5
    });
    layer.bringToFront();
}
function resetHighlight(e) {
    const layer = e.target;
    layer.setStyle({
        color: '#02AAB0',
        weight: 2,
        fillOpacity: 0.2
    });
}
function zoomToFeature(e) {
    const layer = e.target;
    const map = layer._map;
    map.fitBounds(layer.getBounds());
}
function isMonthInRange(month, start, end) {
    if (!start || !end)
        return false;
    if (start <= end) {
        return month >= start && month <= end;
    }
    return month >= start || month <= end;
}
function getCurrentMonth() {
    return new Date().getMonth() + 1;
}
function formatSeasonRange(start, end) {
    if (!start || !end)
        return 'Season dates not provided';
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[start - 1]} – ${monthNames[end - 1]}`;
}
function normalizeRegion(region) {
    if (typeof region === 'string') {
        return region.trim().toLowerCase();
    }
    return (region.value || region.label || '').trim().toLowerCase();
}
function formatRegion(region) {
    if (!region)
        return '';
    if (Array.isArray(region)) {
        return region
            .map(item => item.label || item.value)
            .filter(Boolean)
            .join(', ');
    }
    return region;
}
